import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useVendorList, useUpdatePrintStatus } from "../../hooks";

interface AcceptBillCycleModalProps {
  selectedCycle: any;
  openModal: boolean;
  openChangeModal: (open: boolean) => void;
}

const AcceptBillCycleModal = ({
  selectedCycle,
  openModal,
  openChangeModal,
}: AcceptBillCycleModalProps) => {
  const { toast } = useToast();
  const { data: vendorlist = [] } = useVendorList();
  const updatePrintStatus = useUpdatePrintStatus();

  // Form fields definition
  const formFields: FormField[] = [
    {
      name: "vendor",
      type: "select",
      label: "Vendor",
      placeholder: "Select vendor",
      required: true,
      options: vendorlist.map((vendor) => ({
        value: vendor.value.toString(),
        label: vendor.label,
      })),
      helperText: "Select the vendor to handle this billing cycle",
      className: "col-span-2 mt-3",
    },
    {
      name: "note",
      type: "textarea",
      label: "Note",
      placeholder: "Optional note about this assignment",
      rows: 3,
      required: false,
      className: "col-span-2 mt-3",
    },
  ];

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      vendor: [],
      note: "",
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (openModal) {
      form.reset();
    }
  }, [openModal]);

  // Handle form submission
  const handleSubmit = async (formValues: any) => {
    try {
      const selectedVendorId = formValues.vendor[0];
      const selectedVendorData = vendorlist.find(
        (v) => v.value.toString() === selectedVendorId
      );
      const vendorName = selectedVendorData?.label;

      await updatePrintStatus.mutateAsync({
        runId: selectedCycle.runId,
        payload: {
          vendor: {
            id: selectedVendorId,
            vendor_name: vendorName,
          },
          ...(formValues.note && { note: formValues.note }),
        },
      });

      await queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.bx.printingDistribution.printList(),
      });

      toast({
        title: "Vendor Assigned",
        description: `Billing cycle assigned to ${vendorName}`,
      });

      handleClose();
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description: "Failed to assign vendor. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    openChangeModal(false);
  };

  return (
    <Dialog open={openModal} onOpenChange={openChangeModal}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Accept Billing Cycle</DialogTitle>
          <DialogDescription>
            Assign this billing cycle to a vendor for printing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: updatePrintStatus.isPending
                ? "Assigning..."
                : "Accept & Start Printing",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                buttonContainer: "flex justify-end",
                input:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
                number:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                "multi-select":
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                file:
                  " w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
                documents:
                  "w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptBillCycleModal;
